/**
 * The travel moment between holes (REDESIGN.md 7.8): the world's
 * illustration slides in under a band of its accent color carrying the course
 * name, the place and hole, and (first visit only) the flavor line. 2.4s, or
 * 1.4s once seen; tap anywhere to skip. Reduced motion: a still, 1.4s fade.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { useAssets } from '../../assets';
import type { World } from '../../brand';
import { phoneCrop } from '../../game/layout';
import { Pic, Tibbs, WorldMedallion } from '../Art';

export function Travel({
  world,
  holeText,
  flavor,
  reduced,
  onDone,
}: {
  world: World;
  /** "HOLE 3 OF 9" or "PLAYOFF 1". */
  holeText: string;
  /** Shown only the first time this device visits the world. */
  flavor: boolean;
  reduced: boolean;
  onDone: () => void;
}) {
  const a = useAssets();
  const art = a.worlds.get(world.n);
  const ms = reduced || !flavor ? 1400 : 2400;
  const artBox = useRef<HTMLDivElement>(null);
  const [crop, setCrop] = useState<{ pos: number; origin: number } | null>(null);
  useLayoutEffect(() => {
    const el = artBox.current;
    if (!el || !el.clientHeight) return;
    setCrop(phoneCrop(world.tibbs, el.clientWidth / el.clientHeight));
  }, [world]);
  const done = useRef(onDone);
  done.current = onDone;
  useEffect(() => {
    const t = setTimeout(() => done.current(), ms);
    return () => clearTimeout(t);
  }, [ms]);
  return (
    <div
      className={`travel${reduced ? ' still' : ''}`}
      style={{ '--wc': world.accent, '--dur': `${ms}ms` } as CSSProperties}
      onClick={() => done.current()}
      role="dialog"
      aria-label={`${world.name}, ${world.place}, ${holeText.toLowerCase()}`}
    >
      <div
        className="travel-art"
        ref={artBox}
        style={crop ? ({ '--fx': `${(crop.pos * 100).toFixed(1)}%`, '--ox': `${(crop.origin * 100).toFixed(1)}%` } as CSSProperties) : undefined}
      >
        <Pic
          img={art?.travel}
          portrait={art?.travelPhone}
          sizes="100vw"
          eager
          className="travel-img"
          fallback={
            <div className="travel-fallback" aria-hidden="true">
              <WorldMedallion world={world} size={132} className="travel-medal" />
              <span className="travel-walk">
                <Tibbs pose="wry" size={96} />
              </span>
              <span className="travel-ground" />
            </div>
          }
        />
      </div>
      <div className="travel-band">
        <h2 className="travel-name">{world.name}</h2>
        <p className="travel-place">
          {world.place} · {holeText.toLowerCase()}
        </p>
        {flavor && <p className="travel-flavor">{world.flavor}</p>}
      </div>
      <span className="sr-only">Tap to skip</span>
    </div>
  );
}
